// src/services/leaveRequestsService.js
//
// Leave Requests API service
// ==========================
//
// PURPOSE
// -------
// This file is the thin frontend service layer for leave-request related HTTP calls.
//
// It provides:
//
// Queries
// - GET /leave-requests/my
// - GET /leave-requests/inbox
// - GET /leave-requests/:id
// - GET /leave-requests/approvers
// - GET /leave-requests/cancellable
//
// Commands
// - POST  /leave-requests
// - PATCH /leave-requests/:id/submit
// - PATCH /leave-requests/:id/decision
// - PATCH /leave-requests/:id/cancel
//
// CURRENT APP ARCHITECTURE
// ------------------------
// 1) HTTP is authoritative
//    - backend owns workflow decisions, validation, permissions and status changes
//
// 2) Socket.IO is invalidation-only
//    - backend emits notifications AFTER DB commit
//    - frontend pages refetch authoritative data through HTTP
//
// 3) Auth/session handling is NOT owned here
//    - apiClient attaches Device-Id / Browser-Id / X-Session-Id
//    - apiClient owns refresh-on-401 logic
//    - page / context decides whether to softLogout on 401
//
// Therefore this file stays intentionally THIN:
// - build request
// - normalize returned ids / keys
// - support AbortSignal where useful
// - rethrow errors to callers
//
// BIGINT SAFETY
// -------------
// leave_requests.id may be BIGINT in the backend.
//
// Therefore:
// - keep ids as STRINGS in the frontend service layer
// - do not Number(...) leave ids unless you are absolutely sure it is safe
//
// FILTERING STRATEGY
// ------------------
// Your backend currently provides broad list endpoints:
//
//   GET /leave-requests/my
//   GET /leave-requests/inbox
//
// If backend-side filters are missing or incomplete, frontend may still apply
// final client-side filtering safely.
//
// This service therefore:
// - sends best-effort query params
// - still applies client-side filters as the final guard
//
// ERROR HANDLING POLICY
// ---------------------
// - request cancellation is normal control flow
// - non-cancellation failures are logged and rethrown
// - this file never redirects and never performs logout

import apiClient from "../utils/apiClient";
import { isRequestCanceled } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Route constants                                                            */
/* -------------------------------------------------------------------------- */

const ROUTES = Object.freeze({
  BASE: "/leave-requests",
  MY: "/leave-requests/my",
  INBOX: "/leave-requests/inbox",
  APPROVERS: "/leave-requests/approvers",
  CANCELLABLE: "/leave-requests/cancellable",
  TIME_SETTLEMENT_CANDIDATES: "/leave-requests/time-settlement-candidates",
});

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Convert a value to a trimmed string.
 *
 * @param {any} value
 * @returns {string}
 */
function asString(value) {
  return value == null ? "" : String(value).trim();
}

/**
 * Convert an id-like value to a string or null.
 *
 * IMPORTANT:
 * - used for BIGINT-safe handling
 *
 * @param {any} value
 * @returns {string|null}
 */
function toIdString(value) {
  const s = asString(value);
  return s || null;
}

/**
 * Build canonical leave work item key from id.
 *
 * @param {any} id
 * @returns {string|null}
 */
function toWorkItemKey(id) {
  const s = toIdString(id);
  return s ? `leave:${s}` : null;
}

/**
 * Encode an id path parameter safely.
 *
 * @param {any} id
 * @returns {string}
 */
function encodeIdParam(id) {
  const safe = asString(id);
  if (!safe) {
    throw new Error("id is required.");
  }
  return encodeURIComponent(safe);
}

/**
 * Convert list of statuses into uppercase array.
 *
 * @param {string|string[]|null|undefined} value
 * @returns {string[]|null}
 */
function normalizeStatuses(value) {
  if (value == null) return null;

  const arr = Array.isArray(value) ? value : [value];
  const out = arr
    .map((x) => String(x || "").trim().toUpperCase())
    .filter(Boolean);

  return out.length ? out : null;
}

/**
 * Safe date parsing helper.
 *
 * Returns null when value cannot be parsed.
 *
 * @param {any} value
 * @returns {Date|null}
 */
function toDateOrNull(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Best-effort friendly error message extractor.
 *
 * Useful for UI callers that want to prefer backend validation messages.
 *
 * @param {any} error
 * @param {string} fallback
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = "Request failed.") {
  const msg =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;

  const clean = typeof msg === "string" ? msg.trim() : "";
  return clean || fallback;
}

/* -------------------------------------------------------------------------- */
/* Normalization                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Normalize one leave request DTO.
 *
 * Why:
 * - keep id BIGINT-safe as string
 * - ensure work_item_key exists for callers
 *
 * @param {any} row
 * @returns {object|null}
 */
function normalizeLeaveRequest(row) {
  if (!row || typeof row !== "object") return null;

  const id = toIdString(row.id);

  return {
    ...row,
    id,
    work_item_key: row.work_item_key || toWorkItemKey(id),
  };
}

/**
 * Normalize an array of leave requests.
 *
 * @param {any} data
 * @returns {object[]}
 */
function normalizeLeaveRequestList(data) {
  const list = Array.isArray(data) ? data : [];
  return list.map(normalizeLeaveRequest).filter(Boolean);
}

/* -------------------------------------------------------------------------- */
/* Client-side filtering                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Determine whether a leave request overlaps a requested date range.
 *
 * Rule:
 * - request interval is interpreted from row.start_at to row.end_at
 * - filter values may be YYYY-MM-DD or ISO timestamps
 *
 * We intentionally keep this tolerant:
 * - if parsing fails for a row, we keep the row instead of hiding it unexpectedly
 *
 * @param {object} row
 * @param {string|null} fromISO
 * @param {string|null} toISO
 * @returns {boolean}
 */
function overlapsRange(row, fromISO, toISO) {
  if (!fromISO && !toISO) return true;

  const start = toDateOrNull(row?.start_at);
  const end = toDateOrNull(row?.end_at);

  // If row dates are malformed, do not accidentally hide it.
  if (!start || !end) return true;

  const from = fromISO ? toDateOrNull(fromISO) : null;
  const to = toISO ? toDateOrNull(toISO) : null;

  /**
   * UX rule:
   * if user provides a date-only "to" value, interpret it as inclusive end-of-day
   */
  if (to && String(toISO).length <= 10) {
    to.setHours(23, 59, 59, 999);
  }

  // Overlap logic for half-open-ish practical UI filtering
  if (from && end <= from) return false;
  if (to && start > to) return false;

  return true;
}

/**
 * Final client-side filtering helper.
 *
 * Supported filters:
 * {
 *   status?: string | string[],
 *   from?: string,
 *   to?: string
 * }
 *
 * @param {object[]} list
 * @param {object} filters
 * @returns {object[]}
 */
export function filterLeavesClientSide(list, filters = {}) {
  const rows = Array.isArray(list) ? list : [];

  const statuses = normalizeStatuses(filters.status);
  const from = filters.from ? String(filters.from) : null;
  const to = filters.to ? String(filters.to) : null;

  return rows.filter((row) => {
    if (statuses?.length) {
      const status = String(row?.status || "").trim().toUpperCase();
      if (!statuses.includes(status)) return false;
    }

    if (!overlapsRange(row, from, to)) return false;

    return true;
  });
}

/* -------------------------------------------------------------------------- */
/* Query helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Build optional query params for list endpoints.
 *
 * We send them as best-effort support for current or future backend filtering.
 *
 * @param {object} filters
 * @returns {object|undefined}
 */
function buildListParams(filters = {}) {
  const params = {
    status: filters.status || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };

  const hasAny = Object.values(params).some((v) => v != null && v !== "");
  return hasAny ? params : undefined;
}

/* -------------------------------------------------------------------------- */
/* Queries                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fetch the current user's leave requests.
 *
 * Endpoint:
 *   GET /leave-requests/my
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {object} [options.params]
 * @returns {Promise<object[]>}
 */
export async function fetchMyLeaveRequests(options = {}) {
  try {
    const res = await apiClient.get(ROUTES.MY, {
      withCredentials: true,
      signal: options.signal,
      params: options.params || undefined,
    });

    return normalizeLeaveRequestList(res.data);
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ fetchMyLeaveRequests failed:", error);
    throw error;
  }
}

/**
 * Fetch the current approver inbox.
 *
 * Endpoint:
 *   GET /leave-requests/inbox
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {object} [options.params]
 * @returns {Promise<object[]>}
 */
export async function fetchApproverInbox(options = {}) {
  try {
    const res = await apiClient.get(ROUTES.INBOX, {
      withCredentials: true,
      signal: options.signal,
      params: options.params || undefined,
    });

    return normalizeLeaveRequestList(res.data);
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ fetchApproverInbox failed:", error);
    throw error;
  }
}

/**
 * Fetch one leave request by id.
 *
 * Endpoint:
 *   GET /leave-requests/:id
 *
 * @param {string|number} id
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object|null>}
 */
export async function fetchLeaveRequestById(id, options = {}) {
  try {
    const encoded = encodeIdParam(id);

    const res = await apiClient.get(`${ROUTES.BASE}/${encoded}`, {
      withCredentials: true,
      signal: options.signal,
    });

    return normalizeLeaveRequest(res.data);
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ fetchLeaveRequestById failed:", error);
    throw error;
  }
}

/**
 * Fetch available approvers.
 *
 * Endpoint:
 *   GET /leave-requests/approvers
 *
 * Backend currently returns array directly.
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export async function fetchLeaveApprovers(options = {}) {
  try {
    const res = await apiClient.get(ROUTES.APPROVERS, {
      withCredentials: true,
      signal: options.signal,
    });

    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ fetchLeaveApprovers failed:", error);
    throw error;
  }
}

/**
 * Fetch cancellable leave requests.
 *
 * Endpoint:
 *   GET /leave-requests/cancellable
 *
 * Optional params:
 * - userId   (privileged only)
 * - statuses (array -> CSV)
 * - limit
 *
 * @param {object} [options]
 * @param {string|number} [options.userId]
 * @param {string[]} [options.statuses]
 * @param {number} [options.limit]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export async function listCancellableLeaves(options = {}) {
  try {
    const params = new URLSearchParams();

    if (options.userId != null) {
      params.set("user_id", String(options.userId));
    }

    if (Array.isArray(options.statuses) && options.statuses.length) {
      params.set("statuses", options.statuses.join(","));
    }

    if (options.limit != null) {
      params.set("limit", String(options.limit));
    }

    const url = params.toString()
      ? `${ROUTES.CANCELLABLE}?${params.toString()}`
      : ROUTES.CANCELLABLE;

    const res = await apiClient.get(url, {
      withCredentials: true,
      signal: options.signal,
    });

    return normalizeLeaveRequestList(res.data);
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ listCancellableLeaves failed:", error);
    throw error;
  }
}


/**
 * Fetch candidate requests that can be linked to the currently selected
 * time-balance leave type.
 *
 * This is intentionally a small dedicated endpoint.  The create modal should
 * not infer open private exits or available make-up credits from broad history
 * lists because the backend owns status, remaining minutes, permissions and
 * future calendar-ready settlement rules.
 *
 * @param {object} options
 * @param {number|string} [options.leaveTypeId]
 * @param {string} [options.balanceAccountingType]
 * @param {number|string} [options.targetUserId]
 * @param {number|string} [options.excludeRequestId]
 * @param {number} [options.limit]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function fetchTimeSettlementCandidates(options = {}) {
  try {
    const params = new URLSearchParams();

    if (options.leaveTypeId != null) params.set("leave_type_id", String(options.leaveTypeId));
    if (options.balanceAccountingType) params.set("balance_accounting_type", String(options.balanceAccountingType));
    if (options.targetUserId != null) params.set("target_user_id", String(options.targetUserId));
    if (options.excludeRequestId != null) params.set("exclude_request_id", String(options.excludeRequestId));
    if (options.limit != null) params.set("limit", String(options.limit));

    const url = params.toString()
      ? `${ROUTES.TIME_SETTLEMENT_CANDIDATES}?${params.toString()}`
      : ROUTES.TIME_SETTLEMENT_CANDIDATES;

    const res = await apiClient.get(url, {
      withCredentials: true,
      signal: options.signal,
    });

    return {
      mode_default: res.data?.mode_default || "AUTO_FIFO",
      current_balance_accounting_type: res.data?.current_balance_accounting_type || null,
      opposite_type: res.data?.opposite_type || null,
      target_user_id: toIdString(res.data?.target_user_id),
      candidates: Array.isArray(res.data?.candidates) ? res.data.candidates : [],
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ fetchTimeSettlementCandidates failed:", error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Filtered queries                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Fetch current user's leave requests and apply final client-side filtering.
 *
 * @param {object} [filters]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export async function fetchMyLeaveRequestsFiltered(filters = {}, options = {}) {
  const params = buildListParams(filters);
  const list = await fetchMyLeaveRequests({ ...options, params });
  return filterLeavesClientSide(list, filters);
}

/**
 * Fetch approver inbox and apply final client-side filtering.
 *
 * @param {object} [filters]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object[]>}
 */
export async function fetchApproverInboxFiltered(filters = {}, options = {}) {
  const params = buildListParams(filters);
  const list = await fetchApproverInbox({ ...options, params });
  return filterLeavesClientSide(list, filters);
}

/* -------------------------------------------------------------------------- */
/* Commands                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Create a leave request.
 *
 * Endpoint:
 *   POST /leave-requests
 *
 * Notes:
 * - backend validates workflow / annual limits / overlaps / permissions
 * - backend may return either standard leave request creation
 *   or cancellation-related payloads
 *
 * @param {object} payload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function createLeaveRequest(payload, options = {}) {
  try {
    const res = await apiClient.post(ROUTES.BASE, payload, {
      withCredentials: true,
      signal: options.signal,
    });

    const data = res.data || {};
    const id = toIdString(data.id);

    return {
      ...data,
      id,
      work_item_key: data.work_item_key || toWorkItemKey(id),
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ createLeaveRequest failed:", error);
    throw error;
  }
}

/**
 * Submit a leave-request draft.
 *
 * Endpoint:
 *   PATCH /leave-requests/:id/submit
 *
 * @param {string|number} id
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function submitLeaveRequestDraft(id, options = {}) {
  try {
    const encoded = encodeIdParam(id);

    const res = await apiClient.patch(
      `${ROUTES.BASE}/${encoded}/submit`,
      {},
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    const data = res.data || {};
    const outId = toIdString(data.id ?? id);

    return {
      ...data,
      id: outId,
      work_item_key: data.work_item_key || toWorkItemKey(outId),
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ submitLeaveRequestDraft failed:", error);
    throw error;
  }
}

/**
 * Decide a leave request.
 *
 * Endpoint:
 *   PATCH /leave-requests/:id/decision
 *
 * IMPORTANT:
 * - keep signature compatible with existing callers:
 *     decideLeaveRequest(id, decision, note)
 *
 * @param {string|number} id
 * @param {"APPROVED"|"REJECTED"} decision
 * @param {string|null} note
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function decideLeaveRequest(id, decision, note, options = {}) {
  try {
    const encoded = encodeIdParam(id);

    const res = await apiClient.patch(
      `${ROUTES.BASE}/${encoded}/decision`,
      {
        decision,
        note: note ? String(note) : null,
      },
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    const data = res.data || {};
    const outId = toIdString(data.id ?? id);

    return {
      ...data,
      id: outId,
      work_item_key: data.work_item_key || toWorkItemKey(outId),
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ decideLeaveRequest failed:", error);
    throw error;
  }
}

/**
 * Cancel a leave request.
 *
 * Endpoint:
 *   PATCH /leave-requests/:id/cancel
 *
 * @param {string|number} id
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>}
 */
export async function cancelLeaveRequest(id, options = {}) {
  try {
    const encoded = encodeIdParam(id);

    const res = await apiClient.patch(
      `${ROUTES.BASE}/${encoded}/cancel`,
      {},
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    const data = res.data || {};
    const outId = toIdString(data.id ?? id);

    return {
      ...data,
      id: outId,
      work_item_key: data.work_item_key || toWorkItemKey(outId),
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[leaveRequestsService] ❌ cancelLeaveRequest failed:", error);
    throw error;
  }
}

/* -------------------------------------------------------------------------- */
/* Default export                                                             */
/* -------------------------------------------------------------------------- */

export default {
  fetchMyLeaveRequests,
  fetchApproverInbox,
  fetchLeaveRequestById,
  fetchLeaveApprovers,
  fetchMyLeaveRequestsFiltered,
  fetchApproverInboxFiltered,
  listCancellableLeaves,
  fetchTimeSettlementCandidates,
  createLeaveRequest,
  submitLeaveRequestDraft,
  decideLeaveRequest,
  cancelLeaveRequest,
  filterLeavesClientSide,
  getApiErrorMessage,
};